import type { Ruleset, SourceRef } from "./types.js";

/**
 * The construction library: what a wall, floor, roof, window or door is made
 * of, and the U-value that goes with it.
 *
 * WHY IT EXISTS. A scan measures shape. It cannot see what a wall is made of,
 * so every converted surface arrives with the platform's placeholder U-value
 * (`SCAN_ASSUMPTION_SET`, apps/api/src/survey/scan-conversion.ts) — a number
 * chosen by us, traceable to nothing. Assumed fabric is the largest single
 * source of error in a heat loss: BRE's in-situ measurements found solid walls
 * averaging about 1.6 W/m²K against an assumed 2.1. A designer needs to be
 * able to say "this is a filled cavity in a 1970s house" and have the U-value
 * follow from a published table, rather than typing a number they half
 * remember.
 *
 * WHERE THE FIGURES COME FROM, AND WHERE THEY DO NOT.
 *
 *  - MIS 3005-D:2025 (Issue 2.0, 05/12/2025) tabulates NO U-values. §3.4.1(a),
 *    p.11, requires only that the heat load calculation "shall in other
 *    respects comply with BS EN 12831-12017" [sic]; Table 1 (internal design
 *    temperatures) and Table 2 (outside design temperatures) are temperatures
 *    only. A case-insensitive search of the extracted text for "u-value",
 *    "u value", "thermal transmittance" and "fabric" finds nothing.
 *  - BS EN 12831-1:2017 and its UK National Annex, CIBSE Guide A and the CIBSE
 *    Domestic Heating Design Guide are all paywalled and were NOT read. Nothing
 *    here is attributed to them.
 *  - So every figure in this file comes from **RdSAP 2012 version 9.94
 *    (20th September 2019)**, Appendix S — the published reduced-data SAP
 *    method the UK's own domestic EPCs are produced with. It is the one
 *    freely-readable primary source that tabulates U-values for EXISTING UK
 *    dwellings by construction and age band, and it is where "the age-band
 *    default" in this codebase has always notionally pointed
 *    (`value_provenance` enum: "age-band-default — assumed from construction
 *    age band (RdSAP-style)").
 *
 * WHAT THESE NUMBERS ARE. RdSAP defaults, not measurements. They are what an
 * EPC assessor is told to assume when nobody has measured the element. Picking
 * one is a designer's deliberate choice of a published default — which is why
 * the API stores it as provenance "age-band-default", marks it confirmed (a
 * person chose it), and records the library entry's id alongside, so an
 * auditor can see exactly which table row produced the number. A designer who
 * has a measurement or a manufacturer's figure types it instead, and that
 * stays provenance "surveyor-entered".
 *
 * WHAT IS DELIBERATELY ABSENT.
 *
 *  - **Ground floors.** RdSAP does not tabulate them. §S5.5 (pp.24-25)
 *    CALCULATES the U-value per BS EN ISO 13370 from the floor's area, its
 *    exposed perimeter and the wall thickness; Table S11 (p.25) gives only the
 *    construction and insulation thickness to assume, not a U-value. There is
 *    therefore no sourceable single figure for "suspended floor, no
 *    insulation", and this library does not invent one. A ground floor's
 *    U-value stays a typed figure until the 13370 calculation is built.
 *  - **Uninsulated stone in age bands A-D.** Table S6 prints "a" in those
 *    cells, meaning "use the equations in S5.1.1", which depend on the wall's
 *    thickness (U = 3.3 - 0.002 × mm for granite/whinstone, 3.0 - 0.002 × mm
 *    for sandstone/limestone). No single number exists, so those cells are
 *    `null` here and produce no library entry.
 *  - **Party walls at 0.0 W/m²K.** Table S8B (p.22) gives 0.0 for solid
 *    masonry, timber frame and system-built party walls, and for flats. Zero
 *    is faithful to the table and is kept in the ruleset, but it cannot be
 *    stored on a surface — `surfaces_u_value_sane` requires u_value > 0 — and
 *    a zero U-value is not how this engine expresses "no loss through a party
 *    wall" anyway: that is the other-side temperature, which the designer
 *    enters (docs/research/party-walls-2026-09-11.md). Those rows are excluded
 *    from the selectable library, with the reason on each.
 *
 * ENGLAND AND WALES ONLY, FOR NOW. Table S6 is the England & Wales wall table;
 * Scotland (S7) and Northern Ireland (S8) have their own, and their age bands
 * (Table S1) cover different years. Adding them is a data change, not a code
 * change — see `RDSAP_AGE_BANDS`, which already carries all four columns.
 *
 * Full working, including how the tables were extracted and checked:
 * docs/research/construction-library-2026-09-14.md.
 */

const RDSAP: Omit<SourceRef, "clause" | "page"> = {
  standard: "RdSAP 2012 version 9.94",
  issue: "9.94",
  issued: "2019-09-20",
  url: "https://bregroup.com/documents/d/bre-group/rdsap_2012_9-94-20-09-2019",
};

const source = (clause: string, page: number): SourceRef => ({
  ...RDSAP,
  clause,
  page,
});

// ---------------------------------------------------------------------------
// Age bands — Table S1
// ---------------------------------------------------------------------------

/** RdSAP's construction age bands, A (oldest) to L (newest). */
export type RdsapAgeBand =
  | "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H" | "I" | "J" | "K" | "L";

export const RDSAP_AGE_BAND_ORDER: readonly RdsapAgeBand[] = [
  "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L",
];

export interface AgeBandYears {
  readonly band: RdsapAgeBand;
  /** Years of construction, as printed. Null where the band does not apply. */
  readonly englandWales: string;
  readonly scotland: string;
  readonly northernIreland: string | null;
}

/**
 * Table S1, p.3. The bands are "generally one year after a change in
 * regulations, to allow for completion of dwellings approved under the
 * previous regulations" — which is why they are not round decades.
 */
export const RDSAP_AGE_BANDS: Ruleset<readonly AgeBandYears[]> = {
  id: "rdsap.age-bands",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: source("Table S1", 3),
  data: [
    { band: "A", englandWales: "before 1900", scotland: "before 1919", northernIreland: "before 1919" },
    { band: "B", englandWales: "1900-1929", scotland: "1919-1929", northernIreland: "1919-1929" },
    { band: "C", englandWales: "1930-1949", scotland: "1930-1949", northernIreland: "1930-1949" },
    { band: "D", englandWales: "1950-1966", scotland: "1950-1964", northernIreland: "1950-1973" },
    { band: "E", englandWales: "1967-1975", scotland: "1965-1975", northernIreland: "1974-1977" },
    { band: "F", englandWales: "1976-1982", scotland: "1976-1983", northernIreland: "1978-1985" },
    { band: "G", englandWales: "1983-1990", scotland: "1984-1991", northernIreland: "1986-1991" },
    { band: "H", englandWales: "1991-1995", scotland: "1992-1998", northernIreland: "1992-1999" },
    { band: "I", englandWales: "1996-2002", scotland: "1999-2002", northernIreland: "2000-2006" },
    { band: "J", englandWales: "2003-2006", scotland: "2003-2007", northernIreland: null },
    { band: "K", englandWales: "2007-2011", scotland: "2008-2011", northernIreland: "2007-2013" },
    { band: "L", englandWales: "2012 onwards", scotland: "2012 onwards", northernIreland: "2014 onwards" },
  ],
};

// ---------------------------------------------------------------------------
// Walls — Table S6 (England & Wales)
// ---------------------------------------------------------------------------

/**
 * One row of Table S6: a wall construction and its U-value in each age band.
 * `null` means the table prints no number for that band (see the header).
 */
export interface WallUValueRow {
  /** Stable key; becomes part of the library entry's id. */
  readonly key: string;
  /** The row label, as printed in the table. */
  readonly wallType: string;
  /** Twelve values, in `RDSAP_AGE_BAND_ORDER`. */
  readonly uValues: readonly (number | null)[];
  /** Anything the table's footnotes qualify about this row. */
  readonly note?: string;
}

/**
 * Table S6, p.19 — wall U-values, England and Wales.
 *
 * Footnote markers in the source, and what this file does with them:
 *  * — "wall may have had internal or external insulation when originally
 *      built; this applies only if insulation is known to have been increased
 *      subsequently (otherwise 'as built' applies)". Kept as a row note; it
 *      constrains WHICH row an assessor may use, not the number in it.
 *  † — "assumed as built". Same treatment.
 *  a — see the equations in S5.1.1 (thickness-dependent): stored as null.
 *  b — "or from equations in S5.1.1 if that is less": the tabulated 1.7 is
 *      kept, with the qualification on the row.
 */
export const RDSAP_WALL_U_VALUES: Ruleset<readonly WallUValueRow[]> = {
  id: "rdsap.wall-u-values.england-wales",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales"],
  provenance: "verified",
  source: source("Table S6", 19),
  data: [
    {
      key: "stone-granite-whinstone-as-built",
      wallType: "Stone: granite or whinstone, as built",
      uValues: [null, null, null, null, 1.7, 1.0, 0.6, 0.6, 0.45, 0.35, 0.3, 0.28],
      note:
        "Bands A-D are not tabulated: S5.1.1 gives U = 3.3 - 0.002 × wall thickness in mm. " +
        "Band E's 1.7 applies, or the S5.1.1 figure if that is less.",
    },
    {
      key: "stone-sandstone-limestone-as-built",
      wallType: "Stone: sandstone or limestone, as built",
      uValues: [null, null, null, null, 1.7, 1.0, 0.6, 0.6, 0.45, 0.35, 0.3, 0.28],
      note:
        "Bands A-D are not tabulated: S5.1.1 gives U = 3.0 - 0.002 × wall thickness in mm. " +
        "Band E's 1.7 applies, or the S5.1.1 figure if that is less.",
    },
    {
      key: "solid-brick-as-built",
      wallType: "Solid brick, as built",
      uValues: [1.7, 1.7, 1.7, 1.7, 1.7, 1.0, 0.6, 0.6, 0.45, 0.35, 0.3, 0.28],
    },
    {
      key: "stone-or-solid-brick-50mm-insulation",
      wallType: "Stone or solid brick with 50 mm external or internal insulation",
      uValues: [0.55, 0.55, 0.55, 0.55, 0.55, 0.45, 0.35, 0.35, 0.3, 0.25, 0.21, 0.21],
      note: "Use this row if the wall is known to have added insulation of unknown thickness.",
    },
    {
      key: "stone-or-solid-brick-100mm-insulation",
      wallType: "Stone or solid brick with 100 mm external or internal insulation",
      uValues: [0.32, 0.32, 0.32, 0.32, 0.32, 0.28, 0.24, 0.24, 0.21, 0.19, 0.17, 0.16],
    },
    {
      key: "stone-or-solid-brick-150mm-insulation",
      wallType: "Stone or solid brick with 150 mm external or internal insulation",
      uValues: [0.23, 0.23, 0.23, 0.23, 0.23, 0.21, 0.18, 0.18, 0.17, 0.15, 0.14, 0.14],
    },
    {
      key: "stone-or-solid-brick-200mm-insulation",
      wallType: "Stone or solid brick with 200 mm external or internal insulation",
      uValues: [0.18, 0.18, 0.18, 0.18, 0.18, 0.17, 0.15, 0.15, 0.14, 0.13, 0.12, 0.12],
    },
    {
      key: "cob-as-built",
      wallType: "Cob, as built",
      uValues: [0.8, 0.8, 0.8, 0.8, 0.8, 0.8, 0.6, 0.6, 0.45, 0.35, 0.3, 0.28],
    },
    {
      key: "cob-50mm-insulation",
      wallType: "Cob with 50 mm external or internal insulation",
      uValues: [0.4, 0.4, 0.4, 0.4, 0.4, 0.4, 0.35, 0.35, 0.3, 0.25, 0.21, 0.21],
      note: "Use this row if the wall is known to have added insulation of unknown thickness.",
    },
    {
      key: "cob-100mm-insulation",
      wallType: "Cob with 100 mm external or internal insulation",
      uValues: [0.26, 0.26, 0.26, 0.26, 0.26, 0.26, 0.24, 0.24, 0.21, 0.19, 0.17, 0.16],
    },
    {
      key: "cob-150mm-insulation",
      wallType: "Cob with 150 mm external or internal insulation",
      uValues: [0.2, 0.2, 0.2, 0.2, 0.2, 0.2, 0.18, 0.18, 0.17, 0.15, 0.14, 0.14],
    },
    {
      key: "cob-200mm-insulation",
      wallType: "Cob with 200 mm external or internal insulation",
      uValues: [0.16, 0.16, 0.16, 0.16, 0.16, 0.16, 0.15, 0.15, 0.14, 0.13, 0.12, 0.12],
    },
    {
      key: "cavity-as-built",
      wallType: "Cavity, as built",
      uValues: [1.5, 1.5, 1.5, 1.5, 1.5, 1.0, 0.6, 0.6, 0.45, 0.35, 0.3, 0.28],
    },
    {
      key: "unfilled-cavity-50mm-insulation",
      wallType: "Unfilled cavity with 50 mm external or internal insulation",
      uValues: [0.53, 0.53, 0.53, 0.53, 0.53, 0.45, 0.35, 0.35, 0.3, 0.25, 0.21, 0.21],
      note: "Use this row if the wall is known to have added insulation of unknown thickness.",
    },
    {
      key: "unfilled-cavity-100mm-insulation",
      wallType: "Unfilled cavity with 100 mm external or internal insulation",
      uValues: [0.32, 0.32, 0.32, 0.32, 0.32, 0.3, 0.24, 0.24, 0.21, 0.19, 0.17, 0.16],
    },
    {
      key: "unfilled-cavity-150mm-insulation",
      wallType: "Unfilled cavity with 150 mm external or internal insulation",
      uValues: [0.23, 0.23, 0.23, 0.23, 0.23, 0.21, 0.18, 0.18, 0.17, 0.15, 0.14, 0.14],
    },
    {
      key: "unfilled-cavity-200mm-insulation",
      wallType: "Unfilled cavity with 200 mm external or internal insulation",
      uValues: [0.18, 0.18, 0.18, 0.18, 0.18, 0.17, 0.15, 0.15, 0.14, 0.13, 0.12, 0.12],
    },
    {
      key: "filled-cavity",
      wallType: "Filled cavity",
      uValues: [0.7, 0.7, 0.7, 0.7, 0.7, 0.4, 0.35, 0.35, 0.45, 0.35, 0.3, 0.28],
      note: "Bands I-L are marked 'assumed as built' in the table.",
    },
    {
      key: "filled-cavity-50mm-insulation",
      wallType: "Filled cavity with 50 mm external or internal insulation",
      uValues: [0.37, 0.37, 0.37, 0.37, 0.37, 0.27, 0.25, 0.25, 0.25, 0.25, 0.21, 0.21],
      note: "Use this row if the wall is known to have added insulation of unknown thickness.",
    },
    {
      key: "filled-cavity-100mm-insulation",
      wallType: "Filled cavity with 100 mm external or internal insulation",
      uValues: [0.25, 0.25, 0.25, 0.25, 0.25, 0.2, 0.19, 0.19, 0.19, 0.19, 0.17, 0.16],
    },
    {
      key: "filled-cavity-150mm-insulation",
      wallType: "Filled cavity with 150 mm external or internal insulation",
      uValues: [0.19, 0.19, 0.19, 0.19, 0.19, 0.16, 0.15, 0.15, 0.15, 0.15, 0.14, 0.14],
    },
    {
      key: "filled-cavity-200mm-insulation",
      wallType: "Filled cavity with 200 mm external or internal insulation",
      uValues: [0.16, 0.16, 0.16, 0.16, 0.16, 0.13, 0.13, 0.13, 0.13, 0.13, 0.12, 0.12],
    },
    {
      key: "timber-frame-as-built",
      wallType: "Timber frame, as built",
      uValues: [2.5, 1.9, 1.9, 1.0, 0.8, 0.45, 0.4, 0.4, 0.4, 0.35, 0.3, 0.28],
    },
    {
      key: "timber-frame-internal-insulation",
      wallType: "Timber frame with internal insulation",
      uValues: [0.6, 0.55, 0.55, 0.4, 0.4, 0.4, 0.4, 0.4, 0.4, 0.35, 0.3, 0.28],
      note: "Bands G-L are marked 'assumed as built' in the table.",
    },
    {
      key: "system-build-as-built",
      wallType: "System build, as built",
      uValues: [2.0, 2.0, 2.0, 2.0, 1.7, 1.0, 0.6, 0.6, 0.45, 0.35, 0.3, 0.28],
    },
    {
      key: "system-build-50mm-insulation",
      wallType: "System build with 50 mm external or internal insulation",
      uValues: [0.6, 0.6, 0.6, 0.6, 0.55, 0.45, 0.35, 0.35, 0.3, 0.25, 0.21, 0.21],
      note: "Use this row if the wall is known to have added insulation of unknown thickness.",
    },
    {
      key: "system-build-100mm-insulation",
      wallType: "System build with 100 mm external or internal insulation",
      uValues: [0.35, 0.35, 0.35, 0.35, 0.35, 0.32, 0.24, 0.24, 0.21, 0.19, 0.17, 0.16],
    },
    {
      key: "system-build-150mm-insulation",
      wallType: "System build with 150 mm external or internal insulation",
      uValues: [0.25, 0.25, 0.25, 0.25, 0.25, 0.21, 0.18, 0.18, 0.17, 0.15, 0.14, 0.14],
    },
    {
      key: "system-build-200mm-insulation",
      wallType: "System build with 200 mm external or internal insulation",
      uValues: [0.18, 0.18, 0.18, 0.18, 0.18, 0.17, 0.15, 0.15, 0.14, 0.13, 0.12, 0.12],
    },
  ],
};

// ---------------------------------------------------------------------------
// Party walls — Table S8B
// ---------------------------------------------------------------------------

export interface PartyWallUValue {
  readonly key: string;
  readonly partyWallType: string;
  readonly uValue: number;
}

/**
 * Table S8B, p.22. Faithful to the table, zeros included; the zeros are not
 * offered in the selectable library (see the file header for why).
 */
export const RDSAP_PARTY_WALL_U_VALUES: Ruleset<readonly PartyWallUValue[]> = {
  id: "rdsap.party-wall-u-values",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: source("Table S8B", 22),
  data: [
    { key: "solid-masonry-timber-frame-system-built", partyWallType: "Solid masonry, timber frame or system built", uValue: 0.0 },
    { key: "cavity-masonry-unfilled", partyWallType: "Cavity masonry, unfilled", uValue: 0.5 },
    { key: "cavity-masonry-filled", partyWallType: "Cavity masonry, filled", uValue: 0.2 },
    { key: "unknown-house-or-bungalow", partyWallType: "Unable to determine, house or bungalow", uValue: 0.25 },
    { key: "unknown-flat-or-maisonette", partyWallType: "Unable to determine, flat or maisonette", uValue: 0.0 },
  ],
};

// ---------------------------------------------------------------------------
// Roofs — Tables S9 and S10
// ---------------------------------------------------------------------------

export interface LoftInsulationUValue {
  /** Insulation thickness at joists, mm. 400 means ">= 400". */
  readonly thicknessMm: number;
  readonly slatesOrTiles: number;
  readonly thatched: number;
}

/**
 * Table S9, p.23 — roof U-values when the loft insulation thickness is known.
 * "The U-values in Table S9 take account of joists. The insulation is taken as
 * being between joists only up to 150 mm, and between and over joists for
 * 200 mm or more."
 */
export const RDSAP_ROOF_U_VALUES_BY_LOFT_INSULATION: Ruleset<readonly LoftInsulationUValue[]> = {
  id: "rdsap.roof-u-values.loft-insulation",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: source("Table S9", 23),
  data: [
    { thicknessMm: 0, slatesOrTiles: 2.3, thatched: 0.35 },
    { thicknessMm: 12, slatesOrTiles: 1.5, thatched: 0.32 },
    { thicknessMm: 25, slatesOrTiles: 1.0, thatched: 0.3 },
    { thicknessMm: 50, slatesOrTiles: 0.68, thatched: 0.25 },
    { thicknessMm: 75, slatesOrTiles: 0.5, thatched: 0.22 },
    { thicknessMm: 100, slatesOrTiles: 0.4, thatched: 0.2 },
    { thicknessMm: 150, slatesOrTiles: 0.3, thatched: 0.17 },
    { thicknessMm: 200, slatesOrTiles: 0.21, thatched: 0.14 },
    { thicknessMm: 250, slatesOrTiles: 0.17, thatched: 0.12 },
    { thicknessMm: 270, slatesOrTiles: 0.16, thatched: 0.12 },
    { thicknessMm: 300, slatesOrTiles: 0.14, thatched: 0.11 },
    { thicknessMm: 350, slatesOrTiles: 0.12, thatched: 0.1 },
    { thicknessMm: 400, slatesOrTiles: 0.11, thatched: 0.09 },
  ],
};

export interface RoofUValueRow {
  readonly key: string;
  readonly roofType: string;
  /** Twelve values, in `RDSAP_AGE_BAND_ORDER`. */
  readonly uValues: readonly number[];
  readonly note?: string;
}

/**
 * Table S10, p.24 — assumed roof U-values when Table S9 does not apply.
 *
 * The table's own rows are grouped ("A, B, C, D" share a row); they are
 * expanded here to one value per band so every row is the same shape as
 * Table S6's. The park-home column is not carried: a park home is not a
 * dwelling this platform designs for.
 */
export const RDSAP_ROOF_U_VALUES_BY_AGE_BAND: Ruleset<readonly RoofUValueRow[]> = {
  id: "rdsap.roof-u-values.age-band",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales"],
  provenance: "verified",
  source: source("Table S10", 24),
  data: [
    {
      key: "pitched-insulation-between-joists",
      roofType: "Pitched, slates or tiles, insulation between joists or unknown",
      uValues: [2.3, 2.3, 2.3, 2.3, 1.5, 0.68, 0.4, 0.3, 0.26, 0.16, 0.16, 0.16],
      note:
        "The table prints the assumed insulation thickness alongside: none (A-D), 12 mm (E), " +
        "50 mm (F), 100 mm (G), 150 mm (H and I), 270 mm (J, K and L). " +
        "If the roof insulation is 'none', use U = 2.3 for every roof type.",
    },
    {
      key: "pitched-insulation-at-rafters",
      roofType: "Pitched, slates or tiles, insulation at rafters",
      uValues: [2.3, 2.3, 2.3, 2.3, 1.5, 0.68, 0.4, 0.35, 0.35, 0.2, 0.2, 0.18],
    },
    {
      key: "flat-roof",
      roofType: "Flat roof (and any roof with a sloping ceiling)",
      uValues: [2.3, 2.3, 2.3, 2.3, 1.5, 0.68, 0.4, 0.35, 0.35, 0.25, 0.25, 0.18],
      note: "Band K is 0.20 W/m²K in Scotland — not carried here (England and Wales only).",
    },
    {
      key: "room-in-roof-slates-or-tiles",
      roofType: "Room-in-roof, slates or tiles",
      uValues: [2.3, 2.3, 2.3, 2.3, 1.5, 0.8, 0.5, 0.35, 0.35, 0.3, 0.25, 0.18],
    },
    {
      key: "thatched",
      roofType: "Thatched roof",
      uValues: [0.35, 0.35, 0.35, 0.35, 0.35, 0.35, 0.35, 0.35, 0.35, 0.3, 0.25, 0.18],
    },
    {
      key: "thatched-room-in-roof",
      roofType: "Thatched roof, room-in-roof",
      uValues: [0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.18],
    },
  ],
};

// ---------------------------------------------------------------------------
// Exposed and semi-exposed floors — Table S12
// ---------------------------------------------------------------------------

export interface ExposedFloorUValueRow {
  readonly key: string;
  readonly insulation: string;
  /** Twelve values, in `RDSAP_AGE_BAND_ORDER`. */
  readonly uValues: readonly number[];
  readonly note?: string;
}

/**
 * Table S12, p.26 — exposed and semi-exposed floor U-values.
 *
 * §S5.6: "to simplify data collection no distinction is made in terms of
 * U-value between an exposed floor (to outside air below) and a semi-exposed
 * floor (to an enclosed but unheated space below)". So one set of figures
 * serves both the "exposed" and "over an unheated space" floor types.
 *
 * The table's rows are grouped (A to G, H or I, J, K, L) and are expanded here
 * to one value per band.
 *
 * NOT a ground floor. RdSAP calculates those (see the file header).
 */
export const RDSAP_EXPOSED_FLOOR_U_VALUES: Ruleset<readonly ExposedFloorUValueRow[]> = {
  id: "rdsap.exposed-floor-u-values",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales"],
  provenance: "verified",
  source: source("Table S12", 26),
  data: [
    {
      key: "unknown-or-as-built",
      insulation: "Insulation unknown or as built",
      uValues: [1.2, 1.2, 1.2, 1.2, 1.2, 1.2, 1.2, 0.51, 0.51, 0.25, 0.22, 0.22],
      note: "Band L is 0.18 W/m²K in Scotland — not carried here (England and Wales only).",
    },
    {
      key: "insulated-50mm",
      insulation: "Insulated, 50 mm",
      uValues: [0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.5, 0.25, 0.22, 0.22],
      note: "Use this row if the floor is known to be insulated but the thickness is unknown.",
    },
    {
      key: "insulated-100mm",
      insulation: "Insulated, 100 mm",
      uValues: [0.3, 0.3, 0.3, 0.3, 0.3, 0.3, 0.3, 0.3, 0.3, 0.25, 0.22, 0.22],
    },
    {
      key: "insulated-150mm",
      insulation: "Insulated, 150 mm",
      uValues: [0.22, 0.22, 0.22, 0.22, 0.22, 0.22, 0.22, 0.22, 0.22, 0.22, 0.22, 0.22],
    },
  ],
};

/**
 * §S5.7, p.26 — "The U-value of a floor above partially heated premises is
 * taken as 0.7 W/m²K." One figure, no age band.
 */
export const RDSAP_FLOOR_OVER_PARTIALLY_HEATED_U_VALUE: Ruleset<number> = {
  id: "rdsap.floor-over-partially-heated-u-value",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales", "scotland", "northern-ireland"],
  provenance: "verified",
  source: source("S5.7", 26),
  data: 0.7,
};

// ---------------------------------------------------------------------------
// Windows — Table S14
// ---------------------------------------------------------------------------

export interface WindowCharacteristics {
  readonly key: string;
  readonly glazing: string;
  /** Whole-window U-value, W/(m²·K) — not centre-pane (§S8.2). */
  readonly uValueWindow: number;
  /** The same glazing in a roof window (roof pitch 45°). */
  readonly uValueRoofWindow: number;
  /** Solar transmittance of the glazing. Not used by the heat loss; carried for completeness. */
  readonly gValue: number;
}

/**
 * Table S14, p.27 — window characteristics.
 *
 * §S8.2: "The U-value is for whole window, not centre pane", and may be
 * overwritten "only if documentary evidence is provided, which can be either a
 * Window Energy Rating certificate (as defined by BFRC) or manufacturer's
 * data" — which in this platform is a typed U-value with provenance
 * "surveyor-entered" or "manufacturer".
 *
 * The date splits in the table are RdSAP's England & Wales dates (Scotland and
 * Northern Ireland differ by a year or three).
 */
export const RDSAP_WINDOW_U_VALUES: Ruleset<readonly WindowCharacteristics[]> = {
  id: "rdsap.window-u-values.england-wales",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales"],
  provenance: "verified",
  source: source("Table S14", 27),
  data: [
    { key: "single", glazing: "Single glazed", uValueWindow: 4.8, uValueRoofWindow: 5.1, gValue: 0.85 },
    {
      key: "double-pre-2002-6mm-pvc-or-non-pvc",
      glazing: "Double glazed before 2002, 6 mm gap in a PVC frame or any gap in a non-PVC frame",
      uValueWindow: 3.1, uValueRoofWindow: 3.3, gValue: 0.76,
    },
    {
      key: "double-pre-2002-12mm-pvc",
      glazing: "Double glazed before 2002, 12 mm gap in a PVC frame",
      uValueWindow: 2.8, uValueRoofWindow: 3.0, gValue: 0.76,
    },
    {
      key: "double-pre-2002-16mm-pvc",
      glazing: "Double glazed before 2002, 16 mm or more gap in a PVC frame",
      uValueWindow: 2.6, uValueRoofWindow: 2.8, gValue: 0.76,
    },
    {
      key: "double-2002-or-later",
      glazing: "Double glazed, 2002 or later",
      uValueWindow: 2.0, uValueRoofWindow: 2.2, gValue: 0.72,
    },
    { key: "secondary", glazing: "Secondary glazing", uValueWindow: 2.4, uValueRoofWindow: 2.6, gValue: 0.76 },
    { key: "triple", glazing: "Triple glazed", uValueWindow: 1.8, uValueRoofWindow: 2.0, gValue: 0.68 },
  ],
};

// ---------------------------------------------------------------------------
// Doors — Table S15A
// ---------------------------------------------------------------------------

export interface DoorUValue {
  readonly key: string;
  readonly door: string;
  readonly uValue: number;
  /** Which age bands the row covers; absent means any. */
  readonly ageBands?: readonly RdsapAgeBand[];
}

/** Table S15A, p.28 — doors. England, Wales and Northern Ireland figures. */
export const RDSAP_DOOR_U_VALUES: Ruleset<readonly DoorUValue[]> = {
  id: "rdsap.door-u-values.england-wales",
  version: "1.0.0",
  effectiveFrom: "2019-09-20",
  jurisdictions: ["england", "wales"],
  provenance: "verified",
  source: source("Table S15A", 28),
  data: [
    {
      key: "outside-a-to-j",
      door: "External door, built before 2007",
      uValue: 3.0,
      ageBands: ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"],
    },
    { key: "outside-k", door: "External door, 2007-2011", uValue: 2.0, ageBands: ["K"] },
    { key: "outside-l", door: "External door, 2012 onwards", uValue: 1.8, ageBands: ["L"] },
    { key: "unheated-corridor", door: "Door to an unheated corridor or stairwell", uValue: 1.4 },
  ],
};

// ---------------------------------------------------------------------------
// The selectable library
// ---------------------------------------------------------------------------

/** Which kind of surface a construction can be applied to. */
export type ConstructionElement = "wall" | "floor" | "roof" | "window" | "door";

export interface ConstructionSource {
  readonly standard: string;
  readonly issue: string;
  /** e.g. "Table S6" */
  readonly clause: string;
  /** Page as printed in the document's own footer. */
  readonly page: number;
  readonly url?: string;
}

/** One pickable entry: a construction, its U-value, and where the figure is from. */
export interface Construction {
  /** Stored on the surface (`surfaces.construction_id`). Stable — never re-key. */
  readonly id: string;
  readonly element: ConstructionElement;
  /** What the designer picks, without the age band: "Filled cavity". */
  readonly name: string;
  /** The full label for a list: "Filled cavity — 1967-1975 — 0.70 W/m²K". */
  readonly label: string;
  readonly uValueWPerM2K: number;
  /** Present for the entries that vary by age band. */
  readonly ageBand?: RdsapAgeBand;
  /** Years of construction for that band, as RdSAP prints them. */
  readonly ageBandYears?: string;
  readonly source: ConstructionSource;
  /** Anything the source qualifies about this figure. Shown beside it. */
  readonly note?: string;
}

const AGE_BAND_YEARS = new Map(
  RDSAP_AGE_BANDS.data.map((row) => [row.band, row.englandWales] as const),
);

const toConstructionSource = (ref: SourceRef): ConstructionSource => ({
  standard: ref.standard,
  issue: ref.issue,
  clause: ref.clause,
  // Every ruleset in this file sets a page; the fallback keeps the type honest
  // rather than asserting non-null on a field that is optional in SourceRef.
  page: ref.page ?? 0,
  ...(ref.url !== undefined ? { url: ref.url } : {}),
});

/** "0.70", "1.20" — two decimals, so a list of U-values lines up. */
export function formatUValue(u: number): string {
  return u.toFixed(2);
}

function byAgeBand(
  element: ConstructionElement,
  idPrefix: string,
  ref: SourceRef,
  rows: readonly {
    key: string;
    name: string;
    uValues: readonly (number | null)[];
    note?: string;
  }[],
): Construction[] {
  const out: Construction[] = [];
  for (const row of rows) {
    row.uValues.forEach((u, i) => {
      // A cell the table does not fill in produces no entry: a designer can
      // never pick a U-value this library invented (see the file header).
      if (u === null) return;
      const band = RDSAP_AGE_BAND_ORDER[i]!;
      const years = AGE_BAND_YEARS.get(band)!;
      out.push({
        id: `${idPrefix}.${row.key}.${band}`,
        element,
        name: row.name,
        label: `${row.name} — ${years} — ${formatUValue(u)} W/m²K`,
        uValueWPerM2K: u,
        ageBand: band,
        ageBandYears: years,
        source: toConstructionSource(ref),
        ...(row.note !== undefined ? { note: row.note } : {}),
      });
    });
  }
  return out;
}

/**
 * Every construction a designer can pick, flat.
 *
 * Ordered element by element, and within an element by the source table's own
 * row order then by age band, so the list reads like the table it came from.
 *
 * Two rules hold over every entry, and `constructions.test.ts` enforces both:
 * the U-value is strictly positive (so it can be stored — `surfaces_u_value_sane`),
 * and the id is unique (it is a stored foreign key in all but name).
 */
export const CONSTRUCTION_LIBRARY: readonly Construction[] = [
  ...byAgeBand(
    "wall",
    "rdsap.wall",
    RDSAP_WALL_U_VALUES.source,
    RDSAP_WALL_U_VALUES.data.map((r) => ({
      key: r.key,
      name: r.wallType,
      uValues: r.uValues,
      ...(r.note !== undefined ? { note: r.note } : {}),
    })),
  ),

  // Party walls do not vary by age band, and the two 0.0 rows are left out:
  // zero cannot be stored (`surfaces_u_value_sane`), and "no loss through a
  // party wall" is expressed in this engine by the other-side temperature the
  // designer enters, not by a zero U-value.
  ...RDSAP_PARTY_WALL_U_VALUES.data
    .filter((row) => row.uValue > 0)
    .map((row): Construction => ({
      id: `rdsap.party-wall.${row.key}`,
      element: "wall",
      name: `Party wall: ${row.partyWallType.toLowerCase()}`,
      label: `Party wall: ${row.partyWallType.toLowerCase()} — ${formatUValue(row.uValue)} W/m²K`,
      uValueWPerM2K: row.uValue,
      source: toConstructionSource(RDSAP_PARTY_WALL_U_VALUES.source),
      note:
        "RdSAP gives 0.0 W/m²K for a solid masonry, timber frame or system-built " +
        "party wall, and for any party wall in a flat or maisonette. Those rows are " +
        "not offered here: a stored U-value must be greater than zero, and a party " +
        "wall that loses no heat is said in this design by setting the temperature " +
        "on the other side equal to the room's.",
    })),

  ...byAgeBand(
    "roof",
    "rdsap.roof",
    RDSAP_ROOF_U_VALUES_BY_AGE_BAND.source,
    RDSAP_ROOF_U_VALUES_BY_AGE_BAND.data.map((r) => ({
      key: r.key,
      name: r.roofType,
      uValues: r.uValues,
      ...(r.note !== undefined ? { note: r.note } : {}),
    })),
  ),

  // Measured loft insulation beats the age band: Table S9 is used "where
  // possible", and Table S10 only "in other cases".
  ...RDSAP_ROOF_U_VALUES_BY_LOFT_INSULATION.data.map((row): Construction => {
    const thickness = row.thicknessMm === 0
      ? "no loft insulation"
      : row.thicknessMm === 400
        ? "400 mm or more of loft insulation"
        : `${row.thicknessMm} mm of loft insulation`;
    const name = `Pitched roof, slates or tiles, ${thickness} (measured)`;
    return {
      id: `rdsap.roof.loft-insulation-${row.thicknessMm}mm`,
      element: "roof",
      name,
      label: `${name} — ${formatUValue(row.slatesOrTiles)} W/m²K`,
      uValueWPerM2K: row.slatesOrTiles,
      source: toConstructionSource(RDSAP_ROOF_U_VALUES_BY_LOFT_INSULATION.source),
      note:
        "For a thickness measured at the joists; it beats the age-band figure. " +
        "Takes account of joists: insulation is counted between joists only up to " +
        "150 mm, and between and over joists at 200 mm or more.",
    };
  }),

  ...byAgeBand(
    "floor",
    "rdsap.exposed-floor",
    RDSAP_EXPOSED_FLOOR_U_VALUES.source,
    RDSAP_EXPOSED_FLOOR_U_VALUES.data.map((r) => ({
      key: r.key,
      name: `Exposed or semi-exposed floor, ${r.insulation.toLowerCase()}`,
      uValues: r.uValues,
      ...(r.note !== undefined ? { note: r.note } : {}),
    })),
  ),

  {
    id: "rdsap.floor.over-partially-heated",
    element: "floor",
    name: "Floor over partially heated premises",
    label: `Floor over partially heated premises — ${formatUValue(
      RDSAP_FLOOR_OVER_PARTIALLY_HEATED_U_VALUE.data,
    )} W/m²K`,
    uValueWPerM2K: RDSAP_FLOOR_OVER_PARTIALLY_HEATED_U_VALUE.data,
    source: toConstructionSource(RDSAP_FLOOR_OVER_PARTIALLY_HEATED_U_VALUE.source),
    note:
      "Typically a flat above non-domestic premises that are not heated to the " +
      "same extent or for as long as the flat.",
  },

  ...RDSAP_WINDOW_U_VALUES.data.map((row): Construction => ({
    id: `rdsap.window.${row.key}`,
    element: "window",
    name: row.glazing,
    label: `${row.glazing} — ${formatUValue(row.uValueWindow)} W/m²K`,
    uValueWPerM2K: row.uValueWindow,
    source: toConstructionSource(RDSAP_WINDOW_U_VALUES.source),
    note:
      `Whole window, not centre pane. In a roof window the same glazing is ` +
      `${formatUValue(row.uValueRoofWindow)} W/m²K. Only a BFRC Window Energy ` +
      `Rating certificate or the manufacturer's data replaces this figure.`,
  })),

  ...RDSAP_WINDOW_U_VALUES.data.map((row): Construction => ({
    id: `rdsap.rooflight.${row.key}`,
    element: "window",
    name: `Roof window: ${row.glazing.toLowerCase()}`,
    label: `Roof window: ${row.glazing.toLowerCase()} — ${formatUValue(row.uValueRoofWindow)} W/m²K`,
    uValueWPerM2K: row.uValueRoofWindow,
    source: toConstructionSource(RDSAP_WINDOW_U_VALUES.source),
    note: "Whole window, not centre pane. Tabulated at a 45° roof pitch.",
  })),

  ...RDSAP_DOOR_U_VALUES.data.map((row): Construction => ({
    id: `rdsap.door.${row.key}`,
    element: "door",
    name: row.door,
    label: `${row.door} — ${formatUValue(row.uValue)} W/m²K`,
    uValueWPerM2K: row.uValue,
    source: toConstructionSource(RDSAP_DOOR_U_VALUES.source),
    note:
      "RdSAP takes an insulated door's U-value from documentary evidence " +
      "instead — type that figure in rather than picking a row here.",
  })),
];

const BY_ID = new Map(CONSTRUCTION_LIBRARY.map((c) => [c.id, c] as const));

/** The entry with this id, or undefined — an id from an older bundle may have gone. */
export function constructionById(id: string): Construction | undefined {
  return BY_ID.get(id);
}

/** Every construction that can be applied to this kind of surface. */
export function constructionsForElement(
  element: ConstructionElement,
): readonly Construction[] {
  return CONSTRUCTION_LIBRARY.filter((c) => c.element === element);
}

/**
 * Which library element a survey surface kind takes its constructions from.
 *
 * `rooflight` and `window` share the window list (it carries both the window
 * and the roof-window figure from Table S14). A `ceiling` between storeys is
 * not a roof and has no tabulated figure, so it gets none: returning the roof
 * list would hand a designer a roof U-value for an internal ceiling.
 */
export function constructionElementForSurfaceKind(
  kind: string,
): ConstructionElement | null {
  switch (kind) {
    case "wall":
      return "wall";
    case "floor":
      return "floor";
    case "roof":
      return "roof";
    case "window":
    case "rooflight":
      return "window";
    case "door":
      return "door";
    default:
      return null;
  }
}

/**
 * Why a ground floor has no library entry, in the words the screen uses.
 *
 * Exported rather than written into the portal so the reason travels with the
 * data: if RdSAP ever tabulates one, this constant and the entries appear
 * together.
 */
export const GROUND_FLOOR_NOT_TABULATED =
  "RdSAP does not tabulate a U-value for a floor in contact with the ground, " +
  "solid or suspended: §S5.5 calculates it from the floor's area, its exposed " +
  "perimeter and the wall thickness (BS EN ISO 13370). The floor entries here " +
  "are Table S12's exposed and semi-exposed UPPER floors, which would be the " +
  "wrong figure for this one — so there is nothing to pick, and the U-value " +
  "has to be typed.";
